/**
 * AddOrganizationContactModal
 * Modal for adding contacts to an organization with optional location association
 */

import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createContact } from "../../services/contacts.service";
import { linkContactToLocations } from "../../services/locations.service";
import { useToast } from "../ui/Toast";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import type { Location } from "../../types/organization.types";
import type { UUID } from "../../types/common.types";

interface AddOrganizationContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: UUID;
  locations: Location[];
}

interface ContactFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  title: string;
}

const emptyForm: ContactFormData = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  title: "",
};

const AddOrganizationContactModal: React.FC<
  AddOrganizationContactModalProps
> = ({ isOpen, onClose, organizationId, locations }) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [formData, setFormData] = useState<ContactFormData>(emptyForm);
  const [locationMode, setLocationMode] = useState<"all" | "specific">("all");
  const [selectedLocationIds, setSelectedLocationIds] = useState<UUID[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const showLocationPicker = locations.length >= 2;

  const handleChange = (field: keyof ContactFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleLocationToggle = (locationId: UUID) => {
    setSelectedLocationIds((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId],
    );
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }
    if (
      formData.email &&
      !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
        formData.email,
      )
    ) {
      newErrors.email = "Invalid email format";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveContact = async (): Promise<boolean> => {
    if (!validate()) {
      return false;
    }

    setIsSaving(true);
    try {
      const newContact = await createContact({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim() || undefined,
        phone_number: formData.phone_number.trim() || undefined,
        title: formData.title.trim() || undefined,
        organization_id: organizationId,
      });

      // Link to specific locations if selected
      if (locationMode === "specific" && selectedLocationIds.length > 0) {
        await linkContactToLocations(newContact.id, selectedLocationIds);
      }

      // Invalidate contacts queries
      queryClient.invalidateQueries({ queryKey: ["contacts", organizationId] });
      queryClient.invalidateQueries({
        queryKey: ["organization-stats", organizationId],
      });

      showToast(
        "success",
        `Contact ${formData.first_name} ${formData.last_name} added`,
      );
      return true;
    } catch (error: any) {
      showToast("error", error.message || "Failed to create contact");
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndAddAnother = async () => {
    const success = await saveContact();
    if (success) {
      setFormData(emptyForm);
      setLocationMode("all");
      setSelectedLocationIds([]);
      setErrors({});
    }
  };

  const handleSaveAndClose = async () => {
    const success = await saveContact();
    if (success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData(emptyForm);
    setLocationMode("all");
    setSelectedLocationIds([]);
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Contact" size="md">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            value={formData.first_name}
            onChange={(e) => handleChange("first_name", e.target.value)}
            error={errors.first_name}
            required
          />
          <Input
            label="Last Name"
            value={formData.last_name}
            onChange={(e) => handleChange("last_name", e.target.value)}
            error={errors.last_name}
            required
          />
        </div>

        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          error={errors.email}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Phone"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => handleChange("phone_number", e.target.value)}
          />
          <Input
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            placeholder="e.g. Store Manager"
          />
        </div>

        {/* Location Association */}
        {showLocationPicker && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Location Association
            </label>
            <div className="border border-gray-300 rounded-lg p-4 space-y-3">
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="locationMode"
                  checked={locationMode === "all"}
                  onChange={() => {
                    setLocationMode("all");
                    setSelectedLocationIds([]);
                  }}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-sm font-medium text-gray-900">
                  Organization Level (All Locations)
                </span>
              </label>

              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="locationMode"
                  checked={locationMode === "specific"}
                  onChange={() => setLocationMode("specific")}
                  className="h-4 w-4 text-blue-600"
                />
                <span className="text-sm font-medium text-gray-900">
                  Specific Locations
                </span>
              </label>

              {locationMode === "specific" && (
                <div className="pl-6 space-y-2 border-t border-gray-200 pt-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                    {locations.map((location) => (
                      <label
                        key={location.id}
                        className="flex items-center space-x-2"
                      >
                        <input
                          type="checkbox"
                          checked={selectedLocationIds.includes(location.id)}
                          onChange={() => handleLocationToggle(location.id)}
                          className="h-4 w-4 text-blue-600 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {location.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={handleSaveAndAddAnother}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save & Add Another"}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSaveAndClose}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save & Close"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddOrganizationContactModal;
